import React, { useEffect, useRef, useState } from "react";
import { createOrder, verifyPayment } from "../../../../../../Actions/PaymentActions";
import { fetchFormData } from "../../../../../../Actions/FormActions";
import { fetchShippingStatus } from "../../../../../../Actions/LogisiticsActions";
import { useNavigationBlocker } from "../../../../../../components/NavigationBlocker/NavigationBlocker";
import { useToast } from "../../../../../../components/Toast/ToastContext";

const PaymentDetails = ({ formData, setFormData, formStatus }) => {
  const { showToast } = useToast();
  const { setIsBlocked } = useNavigationBlocker();
  const price = parseInt(process.env.REACT_APP_PRICE);
  const [shippingDetails, setShippingDetails] = useState({
    "shiprocket_order_id": "",
    "awb_code": "",
    "courier_company_id": "",
    "courier_name": "",
    "tracking_id": "",
    "tracking_url": "",
    "created_at": ""
  } || {});

  // Normalize tracking URL to include protocol
  const normalizedTrackingUrl = shippingDetails?.tracking_url && !/^https?:\/\//i.test(shippingDetails?.tracking_url)
    ? `https://${shippingDetails?.tracking_url}`
    : shippingDetails?.tracking_url;

  // Disable navigation blocking while on the PaymentDetails page
  useEffect(() => {
    setIsBlocked(false);
    return () => setIsBlocked(true);
  }, [setIsBlocked]);

  // Polling refs
  const pollingRef = useRef(null);
  const pollingAttemptsRef = useRef(0);

  const startShippingStatusPolling = async (formId) => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }

    const poll = async () => {
      try {
        const res = await fetchShippingStatus(formId);
        // Normalize payload to support both stubbed and real API responses
        const status = res?.data?.status;
        const shipping_details = res?.data?.shipping_details;
        setShippingDetails(shipping_details);

        if (
          (status && (String(status).toLowerCase() === 'paid' || String(status).toLowerCase() === 'paymentverified')) &&
          Object.keys(shipping_details || {}).length > 0
        ) {
          showToast('Shipping details ready ✅', 'success');
          return; // stop polling
        }
      } catch (e) {
        // ignore and continue polling
      }

      // Continue polling with 5-second interval until valid response is received
      pollingAttemptsRef.current += 1;
      pollingRef.current = setTimeout(poll, 5000);
    };

    // initial delay 5 seconds as requested
    pollingAttemptsRef.current = 0;
    pollingRef.current = setTimeout(poll, 5000);
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  // Start polling on reload if payment is done but shipping details are not yet available
  useEffect(() => {
    const hasPaid = Boolean(formData?.payment?.paid_at);
    const missingShipping = !(shippingDetails?.courier_name || shippingDetails?.tracking_id || shippingDetails?.tracking_url || shippingDetails?.shipment_status);
    if (hasPaid && missingShipping && formData?._id && !pollingRef.current) {
      startShippingStatusPolling(formData._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?._id, formData?.payment?.paid_at, shippingDetails?.courier_name, shippingDetails?.tracking_id, shippingDetails?.tracking_url, shippingDetails?.shipment_status]);

  // Remove the useEffect since we'll handle navigation blocking during payment flow
  const handlePayment = async () => {
    try {
      // Step 1: Create order from backend
      const orderData = await createOrder(price, `order_${formData._id}`);
      const { order_id, amount, currency, key } = orderData;
      // Step 2: Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      // Step 3: Open Razorpay Checkout
      const options = {
        key,
        amount: amount * 100, // Razorpay expects amount in paise
        currency,
        name: process.env.REACT_APP_COMPANY_NAME,
        description: process.env.REACT_APP_COMPANY_DESCRIPTION,
        order_id,
        handler: async function (response) {
          try {
            // Step 4: Verify payment
            const verificationResult = await verifyPayment({
              form_id: formData._id,
              amount: amount,
              paid_at: new Date().toISOString(),
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            if (verificationResult.data.status === 'success') {
              // Fetch the latest form data after successful payment
              const updatedFormData = await fetchFormData();
              if (updatedFormData.data) {
                setFormData(updatedFormData.data);
                showToast('Payment successful 🎉', 'success');
                // Begin long-polling shipping status after 5 seconds
                startShippingStatusPolling(formData._id);
              }
            } else {
              alert(`❌ Payment verification failed: ${verificationResult.message || 'Please contact support.'}`);
            }
          } catch (error) {
            console.error("Payment verification failed:", error);
            alert(`❌ Payment verification failed: ${error.message || 'Please contact support.'}`);
          }
        },
        prefill: {
          name: formData.full_name || "Test User",
          email: formData.email || "test@example.com",
          contact: formData.mobile || "9999999999",
        },
        theme: {
          color: "#753ABF",
        },
        modal: {
          ondismiss: function () {
            alert("Payment cancelled by user");
          }
        }
      };

      const rzp = new window.Razorpay(options);
      // Navigation blocking is already disabled on this page
      rzp.open();
    } catch (err) {
      console.error("Payment initialization failed:", err);
      alert("❌ Payment Failed: " + (err.message || "Please try again later"));
    }
  };
  return (
    formStatus.toLowerCase() === "verified" ? (
      <div className="space-y-12">
        <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-bold rounded-md text-green-700 bg-green-100 shadow-sm">
          Please make the payment on the payment link recieved on your email address.
        </div>
      </div>
    ) : formStatus.toLowerCase() === "paid" ? (
      <div className="space-y-12">
        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-bold rounded-md text-green-700 bg-green-100 shadow-sm">
            Payment Completed Successfully 🎉
          </div>
        </div>
      </div>
    ) : (
      <div className="bg-[#D9D9D9] text-black py-4 px- flex flex-col items-center justify-center text-center">
        <span>The <b>payment for Rs. 500 will be available only after verification of the form information</b><br></br> by the Project Sunflower Lanyard Team. Kindly expect a notification on your Email/<br></br>WhatsApp number <b>within 1 week of form submission.</b></span>
        <span className="mt-8">You can contact the team at <a href="mailto:contact@projectsunflowerlanyard.org" className="text-[#753abf]">contact@projectsunflowerlanyard.org</a> or <a href="tel:+918287026805" className="text-[#753abf]">+91-82870 26805</a></span>
      </div>
    )
  )
};

export default PaymentDetails;
